import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Search, Copy, MessageSquare, Mail, Phone, Calendar, Check, Ticket } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuestTicketsTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copiedToken, setCopiedToken] = useState(null);
  const [resendDialog, setResendDialog] = useState(null);
  const [resendPhone, setResendPhone] = useState('');
  const [resending, setResending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [includeUnpaid, setIncludeUnpaid] = useState(false);

  const load = useCallback(async (searchTerm = '', unpaid = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams();
      if (searchTerm) qs.set('search', searchTerm);
      if (unpaid) qs.set('include_unpaid', 'true');
      const url = `${API_URL}/api/admin/guest-tickets${qs.toString() ? '?' + qs.toString() : ''}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.tickets || []);
    } catch {
      toast.error('Failed to load tickets');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load('', includeUnpaid); }, [load, includeUnpaid]);

  const syncFromStripe = async () => {
    if (!window.confirm('Pull recent payments from Stripe and reconcile them with our records? This will update any missed payments.')) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/admin/guest-tickets/sync-from-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      toast.success(
        `Sync complete: ${d.newly_paid} newly marked paid, ${d.created_from_stripe} recovered from Stripe, ${d.already_paid} already paid`,
        { duration: 8000 }
      );
      load(search, includeUnpaid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(search, includeUnpaid);
  };

  const buildMagicLink = (ticket) =>
    `${window.location.origin}/ticket-success?event=${ticket.event_key}&guest=${ticket.guest_token}`;

  const copyLink = async (ticket) => {
    try {
      await navigator.clipboard.writeText(buildMagicLink(ticket));
      setCopiedToken(ticket.guest_token);
      toast.success('Magic link copied — paste it in a text/email to the customer');
      setTimeout(() => setCopiedToken(null), 2500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const openResend = (ticket) => {
    setResendDialog(ticket);
    setResendPhone(ticket.customer_phone || '');
  };

  const doResend = async () => {
    if (!resendDialog) return;
    setResending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/guest-tickets/${resendDialog.guest_token}/resend-sms`,
        { phone: resendPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`SMS sent to ${res.data.sent_to}`);
      setResendDialog(null);
      load(search, includeUnpaid);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SMS failed');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="guest-tickets-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Guest Ticket Buyers</h3>
          <p className="text-sm text-muted-foreground">
            People who paid for event tickets without an account. Use this to recover access for customers who lost their link.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={syncFromStripe} disabled={syncing} variant="outline" data-testid="sync-from-stripe-btn">
            {syncing ? 'Syncing...' : 'Sync from Stripe'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          id="include-unpaid"
          checked={includeUnpaid}
          onChange={(e) => setIncludeUnpaid(e.target.checked)}
          className="cursor-pointer"
          data-testid="include-unpaid-toggle"
        />
        <Label htmlFor="include-unpaid" className="cursor-pointer">
          Show unpaid/pending tickets too (useful for diagnosing missing payments)
        </Label>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, phone, name, or event…"
            className="pl-10"
            data-testid="guest-tickets-search"
          />
        </div>
        <Button type="submit" data-testid="guest-tickets-search-btn">Search</Button>
        {search && (
          <Button type="button" variant="outline" onClick={() => { setSearch(''); load(''); }}>
            Clear
          </Button>
        )}
      </form>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Ticket className="w-12 h-12 mx-auto opacity-40 mb-2" />
          <p>{search ? 'No tickets match that search.' : 'No paid guest tickets yet.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</p>
          {tickets.map(t => (
            <Card key={t.guest_token}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold uppercase">{t.event_title || t.event_key}</span>
                      <Badge variant="secondary">${(t.amount || 0).toFixed(2)}</Badge>
                      {t.paid ? (
                        <Badge className="bg-green-100 text-green-800">Paid</Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800">Pending — webhook missed</Badge>
                      )}
                    </div>
                    <div className="text-sm text-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      {t.customer_name && <span><span className="text-muted-foreground">Name:</span> {t.customer_name}</span>}
                      {t.customer_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-muted-foreground" /> {t.customer_email}
                        </span>
                      )}
                      {t.customer_phone ? (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-muted-foreground" /> {t.customer_phone}
                        </span>
                      ) : (
                        <span className="text-orange-600 text-xs italic">No phone on file</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Paid {t.paid_at ? new Date(t.paid_at).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={copiedToken === t.guest_token ? 'secondary' : 'outline'}
                      onClick={() => copyLink(t)}
                      data-testid={`copy-link-${t.guest_token}`}
                    >
                      {copiedToken === t.guest_token ? (
                        <><Check className="w-3 h-3 mr-1" /> Copied</>
                      ) : (
                        <><Copy className="w-3 h-3 mr-1" /> Copy Link</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openResend(t)}
                      data-testid={`resend-sms-${t.guest_token}`}
                    >
                      <MessageSquare className="w-3 h-3 mr-1" /> Text Link
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resend SMS dialog */}
      <Dialog open={!!resendDialog} onOpenChange={(open) => !open && setResendDialog(null)}>
        <DialogContent className="max-w-md" data-testid="resend-dialog">
          {resendDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight">Text Magic Link</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-3 text-sm">
                  <p className="font-medium">{resendDialog.event_title || resendDialog.event_key}</p>
                  <p className="text-muted-foreground">{resendDialog.customer_name || resendDialog.customer_email}</p>
                </div>
                <div>
                  <Label htmlFor="resend-phone">Phone Number</Label>
                  <Input
                    id="resend-phone"
                    type="tel"
                    value={resendPhone}
                    onChange={(e) => setResendPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    data-testid="resend-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Phone will be saved to the ticket for future re-sends.
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-yellow-900">
                  This sends an SMS via Twilio with the magic link to watch the event.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResendDialog(null)}>Cancel</Button>
                <Button onClick={doResend} disabled={resending || !resendPhone.trim()} data-testid="resend-confirm-btn">
                  {resending ? 'Sending...' : 'Send SMS'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestTicketsTab;
